// General arrangement (GA) drawing support for the printable curve sheet.
// The blank template images (cropped from TAPS's GA drawing PDFs, title
// block stripped off) live in the theme's assets folder as ga-*.png and are
// wired up via window.PUMP_GA_*_URL in pump-selector.liquid, the same
// pattern used for the header/band/footer images.

// Dimension columns per drawing type - only columns that appear on the
// actual GA drawing template are listed, so the printed table matches
// what the engineer can measure on the drawing.
PS.dimensionColumnsByDrawing = {
  // Starline close-coupled, no base
  starline: ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H1', 'H2', 'J', 'K', 'L', 'M1', 'M2', 'N1', 'N2', 'P', 'R', 'S1', 'T1', 'X'],
  // Starline close-coupled, with base
  starlineBase: ['A', 'X', 'H2', 'HS', 'HB', 'HC1', 'HC2', 'HC3', 'BL', 'BW', 'UL', 'BH'],
  // Pedestal/Sovereign/ISO-Pro, no base (isoDimensions rows)
  isoPedestal: ['A', 'B', 'F', 'H1', 'H2', 'L', 'M1', 'M2', 'N1', 'N2', 'N3', 'S1', 'S2', 'T1', 'T2', 'W', 'X'],
  // ISO-Pro/Sovereign, with base (pedestalSpacer rows)
  pedestalBase: ['A', 'H2', 'HS', 'HB', 'HC1', 'HC2', 'HC3', 'BL', 'BW', 'UL', 'BH'],
};

PS.getDimensionTableRows = function (dimensionRow, drawingType) {
  if (!dimensionRow) return [];

  const columns = PS.dimensionColumnsByDrawing[drawingType] || PS.dimensionColumnsByDrawing.starline;

  return columns
    .filter(col => Number.isFinite(Number(dimensionRow[col])) && Number(dimensionRow[col]) !== 0)
    .map(col => [`Dim. ${col}`, `${PS.formatNumber(Number(dimensionRow[col]), 0)} mm`]);
};

// The pump family's size description always leads with "<suction>x<discharge>"
// (e.g. "125x100-200 ISO" for an SFF - 125mm suction, 100mm discharge, 200mm
// nominal impeller, ISO casing standard) - only the starline/pressed-spacer
// dimension tables don't carry their own Sucdia/Disdia columns, so this is
// the fallback source for those.
PS.parsePumpSizeDiameters = function (match) {
  const pumpSize = PS.getPumpSizeDescription(match);
  const parsed = /^(\d+)\s*x\s*(\d+)/i.exec(pumpSize || '');

  if (!parsed) return { suctionMm: null, dischargeMm: null };

  return { suctionMm: Number(parsed[1]), dischargeMm: Number(parsed[2]) };
};

// Onga's fixed-motor domestic pumps don't have a mounting type, swappable
// motor, or a shared blank GA template to overlay numbers onto - each
// family's datasheet drawing is laid out differently, so rather than
// digitising every family into the lettered A-F-style dimension table (see
// ROADMAP.md's "Onga Dimension Data - proposed direction change"), this
// shows the datasheet's own drawing image directly when one has been
// uploaded. Returns null (same as "no drawing available") until a
// gaDrawingUrl is added for that model in pump-selector-onga-dimensions.json.
PS.getOngaGaDrawing = function (match) {
  const dimRow = PS.getOngaDimensionRow(match);

  if (!dimRow?.gaDrawingUrl) return null;

  const unitWeight = Number(dimRow.weightKg);
  const weightText = Number.isFinite(unitWeight) && unitWeight > 0 ? `${PS.formatNumber(unitWeight, 1)} kg` : '-';

  return {
    imageUrl: dimRow.gaDrawingUrl,
    title: `GA Drawing - ${PS.getPumpDisplayName(match)}`,
    fields: [
      ['Pump Model', PS.getPumpDisplayName(match)],
      ['Inlet', dimRow.inlet || '-'],
      ['Outlet', dimRow.outlet || '-'],
      ['Unit Weight', weightText]
    ],
    dimensionTableRows: []
  };
};

// Same idea as PS.getOngaGaDrawing, for Jung's own model-keyed dimension file
// (pump-selector-jung-dimensions.json) - kept as its own function rather than
// generalising PS.getOngaGaDrawing itself, so Onga's existing behaviour stays
// provably untouched (mirrors how Onga Intellimaster's speedAdjustable path
// was kept fully isolated rather than reusing/branching shared logic).
PS.getJungGaDrawing = function (match) {
  const dimRow = PS.getJungDimensionRow(match);

  if (!dimRow?.gaDrawingUrl) return null;

  const unitWeight = Number(dimRow.weightKg);
  const weightText = Number.isFinite(unitWeight) && unitWeight > 0 ? `${PS.formatNumber(unitWeight, 1)} kg` : '-';

  return {
    imageUrl: dimRow.gaDrawingUrl,
    title: `GA Drawing - ${PS.getPumpDisplayName(match)}`,
    fields: [
      ['Pump Model', PS.getPumpDisplayName(match)],
      ['Inlet', dimRow.inlet || '-'],
      ['Outlet', dimRow.outlet || '-'],
      ['Unit Weight', weightText]
    ],
    dimensionTableRows: []
  };
};

// Works out which of the five GA drawing templates applies to this match,
// and gathers everything the curve sheet needs to render it: the
// background image, the trimmed title block fields (no Job/Installation
// Reference/Date of Issue - those aren't used), and the dimension table.
PS.getGaDrawing = function (match) {
  if (match.fixedPump || match.curve?.fixedPump) {
    const manufacturer = match.curve?.manufacturer || match.manufacturer;
    return manufacturer === 'Jung Pumpen' ? PS.getJungGaDrawing(match) : PS.getOngaGaDrawing(match);
  }

  const mountingType = PS.getEffectiveMountingType(match);
  const motor = PS.getEffectiveMotor(match);
  const dimensionRow = PS.findDimensionRow(match, mountingType, motor);

  if (!dimensionRow) return null;

  const pumpCode = PS.getDimensionPumpCode(match.family);
  const unitWeight = Number(dimensionRow.Unitweight);
  const weightText = Number.isFinite(unitWeight) && unitWeight > 0 ? `${PS.formatNumber(unitWeight, 0)} kg` : '-';

  const parsedDiameters = PS.parsePumpSizeDiameters(match);

  if (mountingType === 'isoPro' || mountingType === 'pwt') {
    const sucDia = Number(dimensionRow.Sucdia) || parsedDiameters.suctionMm;
    const disDia = Number(dimensionRow.Disdia) || parsedDiameters.dischargeMm;
    const shaftDiaSov = Number(dimensionRow.Dsov);
    const shaftDiaPro = Number(dimensionRow.Dpro2);
    const dimensionTableRows = PS.getDimensionTableRows(dimensionRow, 'isoPedestal');

    const fields = [
      ['Pump Model', PS.getPumpDisplayName(match)],
      ['Speed Range', `${match.selectedSpeedRpm ? PS.formatNumber(match.selectedSpeedRpm, 0) + ' RPM' : '-'}`],
      ['Suction Diameter', Number.isFinite(sucDia) && sucDia > 0 ? `DN${sucDia}` : '-'],
      ['Discharge Diameter', Number.isFinite(disDia) && disDia > 0 ? `DN${disDia}` : '-'],
    ];

    if (Number.isFinite(shaftDiaSov) && shaftDiaSov > 0) {
      fields.push(['Pump Shaft Diameter', `${PS.formatNumber(shaftDiaSov, 0)} mm`]);
    }
    if (Number.isFinite(shaftDiaPro) && shaftDiaPro > 0) {
      fields.push(['Motor Shaft Diameter', `${PS.formatNumber(shaftDiaPro, 0)} mm`]);
    }

    fields.push(
      ['Pump Code', pumpCode],
      ['Pump Weight', weightText],
      ['Curve Reference', match.family]
    );

    return {
      imageUrl: (mountingType === 'pwt' ? window.PUMP_GA_PWT_URL : window.PUMP_GA_ISOPRO_URL) || '',
      title: mountingType === 'pwt' ? 'GA Drawing - PWT Pump' : 'GA Drawing - Iso-Pro Pump',
      fields,
      dimensionTableRows
    };
  }

  // The download modal's Base dropdown (No Base / Base / Fabricated Base)
  // lets staff override which drawing gets used - "Fabricated Base" has no
  // dedicated drawing yet, so it falls back to the standard based template
  // until that's built.
  const baseType = document.getElementById('curve-export-base-type')?.value || null;
  const isBased = mountingType === 'starline'
    ? (baseType ? baseType !== 'noBase' : dimensionRow._source === 'starlineBase')
    : true;

  const drawingType = mountingType === 'starline'
    ? (isBased ? 'starlineBase' : 'starline')
    : 'pedestalBase';

  const dimensionTableRows = PS.getDimensionTableRows(dimensionRow, drawingType);

  const nHoles = Number(dimensionRow.NHoles) || 2;
  const starlineBaseUrl = nHoles === 3
    ? (window.PUMP_GA_STARLINE_BASE_3HOLE_URL || '')
    : (window.PUMP_GA_STARLINE_BASE_2HOLE_URL || '');

  const imageUrl = mountingType === 'starline'
    ? (isBased ? starlineBaseUrl : (window.PUMP_GA_STARLINE_URL || ''))
    : (window.PUMP_GA_PRESSED_SPACER_URL || '');

  const title = mountingType === 'starline'
    ? `GA Drawing - Starline Motorpump${isBased ? ' (with base)' : ''}`
    : 'GA Drawing - Long Coupled Motorpump (pressed spacer base)';

  const unitCode = dimensionRow.Unitcode || '-';

  return {
    imageUrl,
    title,
    fields: [
      ['Pump Model', PS.getPumpDisplayName(match)],
      ['Motor', motor ? `${PS.formatNumber(motor.powerKw, motor.powerKw < 10 ? 1 : 0)} kW, ${motor.poles} pole` : '-'],
      ['Suction Diameter', parsedDiameters.suctionMm ? `DN${parsedDiameters.suctionMm}` : '-'],
      ['Discharge Diameter', parsedDiameters.dischargeMm ? `DN${parsedDiameters.dischargeMm}` : '-'],
      ['Pump Unit Code', unitCode],
      ['Unit Weight', weightText],
      ['Curve Reference', match.family]
    ],
    dimensionTableRows
  };
};
